import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  BookOpen,
  MessageSquare,
  GraduationCap,
  Users,
  Settings,
  UserCog,
  FileText,
  X,
  Award,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  ChevronDown,
  Wrench,
  Tag,
  History,
  Receipt,
  CreditCard,
  Package,
} from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import { BrandLogo } from "@/components/BrandLogo";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth, type UiRole } from "@/auth/AuthContext";

interface AppSidebarProps {
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

// Leaf nav item
interface NavItem {
  type: "item";
  id: string;
  icon: React.ElementType;
  label: string;
}

// Collapsible group
interface NavGroup {
  type: "group";
  id: string;
  icon: React.ElementType;
  label: string;
  children: NavItem[];
}

// Section divider (label + list of items/groups)
interface NavSection {
  type: "section";
  label?: string;
  entries: (NavItem | NavGroup)[];
}

type NavTree = NavSection[];

const navByRole: Record<UiRole, NavTree> = {
  student: [
    {
      type: "section",
      entries: [
        { type: "item", id: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
        { type: "item", id: "/community", icon: MessageSquare, label: "Community" },
        { type: "item", id: "/courses", icon: BookOpen, label: "My Courses" },
        { type: "item", id: "/mock-tests", icon: GraduationCap, label: "Mock Tests" },
        { type: "item", id: "/mock-test-bundles", icon: Package, label: "Bundles" },
        { type: "item", id: "/exam-history", icon: History, label: "Exam History" },
        { type: "item", id: "/certificates", icon: Award, label: "Certificates" },
      ],
    },
  ],

  teacher: [
    {
      type: "section",
      entries: [
        { type: "item", id: "/teacher", icon: LayoutDashboard, label: "Dashboard" },
        { type: "item", id: "/community", icon: MessageSquare, label: "Community" },
        { type: "item", id: "/teacher/courses", icon: BookOpen, label: "Courses" },
        { type: "item", id: "/mock-tests", icon: ClipboardList, label: "Mock Tests" },
        { type: "item", id: "/teacher/coupons", icon: Tag, label: "Coupons" },
        { type: "item", id: "/teacher/students", icon: Users, label: "Students" },
      ],
    },
  ],

  admin: [
    {
      type: "section",
      entries: [
        { type: "item", id: "/admin", icon: LayoutDashboard, label: "Dashboard" },
        { type: "item", id: "/community", icon: MessageSquare, label: "Community" },
        { type: "item", id: "/admin/courses", icon: BookOpen, label: "Courses" },
        { type: "item", id: "/mock-tests", icon: ClipboardList, label: "Mock Tests" },
        { type: "item", id: "/admin/mock-test-purchases", icon: Users, label: "Test Purchases" },
        { type: "item", id: "/admin/mock-test-bundles", icon: Package, label: "Bundles" },
        { type: "item", id: "/admin/mock-test-subscriptions", icon: CreditCard, label: "Subscriptions" },
        { type: "item", id: "/admin/coupons", icon: Tag, label: "Coupons" },
        { type: "item", id: "/admin/payments", icon: Receipt, label: "Payments & Refunds" },
        { type: "item", id: "/admin/users", icon: Users, label: "Users" },
        { type: "item", id: "/admin/certificates", icon: Award, label: "Certificates" },
      ],
    },
    {
      type: "section",
      label: "System",
      entries: [
        { type: "item", id: "/settings", icon: Settings, label: "Settings" },
        { type: "item", id: "/admin/roles", icon: UserCog, label: "Roles & Permissions" },
        { type: "item", id: "/admin/exam-config", icon: FileText, label: "Exam Config" },
      ],
    },
  ],
};

function useActivePath() {
  const location = useLocation();
  return (path: string) => {
    if (path === "/admin" || path === "/teacher" || path === "/dashboard") {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };
}

// Determine which groups should be expanded by default based on current route
function getDefaultOpenGroups(sections: NavTree, isActive: (path: string) => boolean): Set<string> {
  const open = new Set<string>();
  for (const section of sections) {
    for (const entry of section.entries) {
      if (entry.type === "group") {
        if (entry.children.some((c) => isActive(c.id))) {
          open.add(entry.id);
        }
      }
    }
  }
  return open;
}

export function AppSidebar({ open, onClose, collapsed, onToggleCollapse }: AppSidebarProps) {
  const { user } = useAuth();
  const role: UiRole = user?.role ?? "student";
  const permissions = user?.permissions ?? ({} as Partial<Record<string, boolean>>);
  const isActive = useActivePath();

  // Build nav tree
  const baseSections: NavTree = navByRole[role] ?? navByRole.student;
  const sections: NavTree = [...baseSections];

  // Non-admin users with special permissions: inject extra items
  if (role !== "admin") {
    const extraItems: NavItem[] = [];
    if (permissions.create_course || permissions.edit_course) {
      extraItems.push({ type: "item", id: "/admin/courses", icon: BookOpen, label: "All Courses" });
    }
    if (permissions.manage_certificates) {
      extraItems.push({ type: "item", id: "/admin/certificates", icon: Award, label: "Certificates" });
    }
    if (extraItems.length) {
      sections.push({ type: "section", label: "Manage", entries: extraItems });
    }
  }

  const [openGroups, setOpenGroups] = useState<Set<string>>(() =>
    getDefaultOpenGroups(sections, isActive),
  );

  // Re-open groups when route changes (e.g. user navigates via URL)
  useEffect(() => {
    setOpenGroups(getDefaultOpenGroups(sections, isActive));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, role]);

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Is any child of a group active?
  const isGroupActive = (group: NavGroup) => group.children.some((c) => isActive(c.id));

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-foreground/30 backdrop-blur-[1px] z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={cn(
          "fixed lg:sticky top-0 left-0 z-50 h-screen flex flex-col transition-all duration-300 ease-in-out",
          "bg-sidebar text-sidebar-foreground border-r border-sidebar-border",
          collapsed ? "w-[60px]" : "w-[220px]",
          "lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {/* Collapse toggle — desktop only */}
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex absolute -right-3 top-4 z-10 w-6 h-6 items-center justify-center rounded-full border border-sidebar-border bg-card text-muted-foreground hover:text-foreground shadow-sm transition-colors"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
        </button>

        {/* Logo / Brand */}
        <div
          className={cn(
            "flex items-center h-[64px] shrink-0 px-3",
            collapsed ? "justify-center" : "justify-between",
          )}
        >
          {collapsed ? (
            <Link to="/" aria-label="Home">
              <BrandLogo size="md" showText={false} />
            </Link>
          ) : (
            <BrandLogo asLinkTo="/" size="md" />
          )}
          <button
            onClick={onClose}
            className="lg:hidden text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close sidebar"
          >
            <X size={16} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-4 scrollbar-thin">
          {sections.map((section, si) => (
            <div key={si}>
              {/* Section label */}
              {!collapsed && section.label && (
                <p className="text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-[0.12em] mb-1 px-2">
                  {section.label}
                </p>
              )}

              <div className="space-y-0.5">
                {section.entries.map((entry) => {
                  if (entry.type === "item") {
                    return (
                      <NavItemRow
                        key={entry.id}
                        item={entry}
                        active={isActive(entry.id)}
                        collapsed={collapsed}
                        onClose={onClose}
                      />
                    );
                  }

                  // Group
                  const groupActive = isGroupActive(entry);
                  const groupOpen = openGroups.has(entry.id);

                  if (collapsed) {
                    // In collapsed mode: show only the group icon, tooltip with label
                    return (
                      <Tooltip key={entry.id} delayDuration={0}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => toggleGroup(entry.id)}
                            className={cn(
                              "w-full flex items-center justify-center p-2 rounded-lg transition-colors duration-150",
                              groupActive
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground",
                            )}
                          >
                            <entry.icon size={16} className="shrink-0" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="font-medium text-xs">
                          {entry.label}
                        </TooltipContent>
                      </Tooltip>
                    );
                  }

                  return (
                    <div key={entry.id}>
                      {/* Group header */}
                      <button
                        onClick={() => toggleGroup(entry.id)}
                        className={cn(
                          "w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-sm transition-colors duration-150",
                          groupActive && !groupOpen
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground",
                        )}
                        aria-expanded={groupOpen}
                      >
                        <entry.icon size={16} className="shrink-0" />
                        <span className="flex-1 text-left leading-none">{entry.label}</span>
                        <ChevronDown
                          size={13}
                          className={cn(
                            "shrink-0 transition-transform duration-200",
                            groupOpen ? "rotate-0" : "-rotate-90",
                          )}
                        />
                      </button>

                      {/* Children */}
                      <div
                        className={cn(
                          "overflow-hidden transition-all duration-200 ease-in-out",
                          groupOpen ? "max-h-60 opacity-100" : "max-h-0 opacity-0",
                        )}
                      >
                        <div className="mt-0.5 ml-3 pl-3 border-l border-sidebar-border space-y-0.5 pb-0.5">
                          {entry.children.map((child) => (
                            <NavItemRow
                              key={child.id}
                              item={child}
                              active={isActive(child.id)}
                              collapsed={false}
                              onClose={onClose}
                              isChild
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}

// ─── NavItemRow ────────────────────────────────────────────────────────────────
interface NavItemRowProps {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onClose: () => void;
  isChild?: boolean;
}

function NavItemRow({ item, active, collapsed, onClose, isChild }: NavItemRowProps) {
  const linkEl = (
    <Link
      to={item.id}
      onClick={onClose}
      className={cn(
        "flex items-center gap-2.5 rounded-lg text-sm transition-colors duration-150 w-full",
        collapsed ? "justify-center p-2" : isChild ? "px-2 py-1.5" : "px-2 py-1.5",
        active
          ? "bg-primary/10 text-primary font-medium"
          : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground",
      )}
    >
      <item.icon size={isChild ? 14 : 16} className="shrink-0" />
      {!collapsed && <span className="truncate leading-none">{item.label}</span>}
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{linkEl}</TooltipTrigger>
        <TooltipContent side="right" className="font-medium text-xs">
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return linkEl;
}
